import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Card, Input, Spinner } from "../../shared/ui";
import { Plus, Trash2, Shield, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { value: "crane", label: "Crane (HRWL)" },
  { value: "rigging", label: "Dogging / Rigging" },
  { value: "ewp", label: "EWP" },
  { value: "forklift", label: "Forklift" },
  { value: "earthmoving", label: "Earthmoving" },
  { value: "driving", label: "Driving Licence" },
  { value: "safety", label: "Safety / Site" },
  { value: "custom", label: "Custom" },
];

/**
 * Master list of tickets / licenses. Seeded by /api/ticket-types/seed
 * with the Aussie HRWL standard set. Admin/manager/office can add more.
 */
export const TicketTypesPanel = ({ currentUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTicket, setNewTicket] = useState({ code: "", name: "", category: "custom", issuer: "" });
  const [saving, setSaving] = useState(false);

  const canManage = ["admin", "manager", "office_staff"].includes(currentUser?.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ticket-types`);
      setItems(r.data || []);
    } catch {
      toast.error("Couldn't load ticket types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const reSeed = async () => {
    try {
      const r = await axios.post(`${API}/ticket-types/seed`);
      toast.success(`Seeded ${r.data.inserted} new tickets`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't seed");
    }
  };

  const addTicket = async () => {
    if (!newTicket.code || !newTicket.name) {
      toast.error("Code and name required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/ticket-types`, newTicket);
      toast.success("Ticket added");
      setNewTicket({ code: "", name: "", category: "custom", issuer: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add");
    } finally {
      setSaving(false);
    }
  };

  const deleteTicket = async (id) => {
    if (!window.confirm("Delete this ticket type?")) return;
    try {
      await axios.delete(`${API}/ticket-types/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  const grouped = items.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="ticket-types-panel">
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold uppercase flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-500" /> Tickets & Licenses
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Master list of tickets the system uses to gate machine operation.
              {items.length} total · {items.filter((i) => i.is_custom).length} custom
            </p>
          </div>
          {canManage && (
            <Button size="sm" variant="ghost" onClick={reSeed} data-testid="ticket-reseed-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Re-seed Standard List
            </Button>
          )}
        </div>

        {canManage && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
            <div className="text-xs uppercase font-bold text-orange-700 mb-2">Add Custom Ticket</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Input placeholder="CODE (e.g. EWP_PINK)" value={newTicket.code}
                onChange={(e) => setNewTicket({ ...newTicket, code: e.target.value.toUpperCase() })}
                className="h-9 text-sm" data-testid="new-ticket-code" />
              <Input placeholder="Name (e.g. EWP Pink Card)" value={newTicket.name}
                onChange={(e) => setNewTicket({ ...newTicket, name: e.target.value })}
                className="h-9 text-sm" data-testid="new-ticket-name" />
              <select value={newTicket.category}
                onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                className="h-9 text-sm bg-white border-2 border-slate-200 rounded-lg px-2"
                data-testid="new-ticket-cat">
                {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <Input placeholder="Issuer (optional)" value={newTicket.issuer}
                onChange={(e) => setNewTicket({ ...newTicket, issuer: e.target.value })}
                className="h-9 text-sm" data-testid="new-ticket-issuer" />
            </div>
            <Button size="sm" onClick={addTicket} disabled={saving} className="mt-2" data-testid="new-ticket-add">
              <Plus className="w-4 h-4 mr-1" /> {saving ? "Adding..." : "Add Ticket"}
            </Button>
          </div>
        )}

        {loading ? <Spinner /> : (
          <div className="space-y-3">
            {Object.keys(grouped).sort().map((cat) => (
              <div key={cat}>
                <div className="text-xs uppercase font-bold text-slate-500 mb-1">{cat}</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {grouped[cat].map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded">
                      <div>
                        <div className="text-sm font-semibold">{t.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{t.code}{t.issuer && ` · ${t.issuer}`}</div>
                      </div>
                      {canManage && t.is_custom && (
                        <button onClick={() => deleteTicket(t.id)} className="text-red-500 hover:text-red-700" data-testid={`del-ticket-${t.code}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default TicketTypesPanel;
